/* Slow, optional motion on a native scroll region. No content depends on animation. */
(() => {
 if (customElements.get('mm-logo-carousel')) return;
 customElements.define('mm-logo-carousel', class extends HTMLElement {
  connectedCallback() {
   if(this.connected)return;
   this.viewport=this.querySelector('[data-logo-viewport]');this.list=this.querySelector('[data-logo-list]');
   if(!this.viewport||!this.list)return;
   this.connected=true;
   this.motion=matchMedia('(prefers-reduced-motion: reduce)');
   this.automatic=this.dataset.autoScroll==='true'&&this.dataset.designMode!=='true';
   this.temporaryPaused=false;this.resumeTimer=null;
   this.hovered=false;this.focused=false;this.pointerHeld=false;this.pointerFocus=false;this.visible=true;
   this.listeners=[];
   const on=(target,type,handler,options)=>{target.addEventListener(type,handler,options);this.listeners.push(()=>target.removeEventListener(type,handler,options));};
   this.clone=this.list.cloneNode(true);this.clone.removeAttribute('data-logo-list');this.clone.setAttribute('data-logo-clone','');this.clone.setAttribute('aria-hidden','true');this.clone.setAttribute('inert','');
   this.clone.querySelectorAll('[data-shopify-editor-block]').forEach(el=>el.removeAttribute('data-shopify-editor-block'));
   this.viewport.append(this.clone);
   this.logoItems=[...this.list.children,...this.clone.children];
   // Width and gap change on resize, not on each animation frame. Read them
   // together here so autoplay only writes scrollLeft between resize events.
   this.measureLoop=()=>{this.loopPixels=this.list.scrollWidth+(parseFloat(getComputedStyle(this.viewport).columnGap)||0);};
   this.measureLoop();
   if('ResizeObserver' in window){this.resizeObserver=new ResizeObserver(this.measureLoop);this.resizeObserver.observe(this.list);this.resizeObserver.observe(this.viewport);}
   on(window,'resize',this.measureLoop,{passive:true});
   const previous=this.querySelector('[data-logo-prev]'),next=this.querySelector('[data-logo-next]');
   previous.hidden=false;next.hidden=false;
   on(previous,'click',()=>this.move(-1));on(next,'click',()=>this.move(1));
   on(this.viewport,'pointerdown',()=>{this.pointerFocus=true;this.focused=false;this.pointerHeld=true;this.pauseTemporarily();});
   const releasePointer=()=>{if(this.pointerHeld){this.pointerHeld=false;this.pauseTemporarily();}};
   on(window,'pointerup',releasePointer);on(window,'pointercancel',releasePointer);
   on(this.viewport,'wheel',()=>this.pauseTemporarily(),{passive:true});
   on(this.viewport,'keydown',event=>{this.pointerFocus=false;this.focused=this.viewport.contains(document.activeElement);if(['ArrowLeft','ArrowRight','Home','End','PageUp','PageDown'].includes(event.key))this.pauseTemporarily();else this.sync();});
   // The side arrows do not hold an indefinite hover/focus pause after a click.
   on(this.viewport,'mouseenter',()=>{this.hovered=true;this.sync();});
   on(this.viewport,'mouseleave',()=>{this.hovered=false;this.setHoveredLogo(null);this.sync();});
   // The loop copy stays inert. Use pointer coordinates on its scroll region to
   // give a visible copied logo the same tint without adding announced content.
   on(this.viewport,'pointermove',event=>{
    const item=event.pointerType==='touch'?null:this.logoItems.find(logo=>{
     const bounds=logo.getBoundingClientRect();
     return bounds.width>0&&event.clientX>=bounds.left&&event.clientX<bounds.right&&event.clientY>=bounds.top&&event.clientY<bounds.bottom;
    });
    this.setHoveredLogo(item||null);
   });
   on(this.viewport,'pointerleave',()=>this.setHoveredLogo(null));
   on(this.viewport,'focusin',()=>{this.focused=!this.pointerFocus;this.sync();});
   on(this.viewport,'focusout',event=>{const within=!!event.relatedTarget&&this.viewport.contains(event.relatedTarget);this.focused=within&&!this.pointerFocus;if(!within)this.pointerFocus=false;this.sync();});
   on(document,'visibilitychange',()=>this.sync());
   on(this.motion,'change',()=>this.sync());
   this.tick=time=>{
    if(!this.connected)return;
    const elapsed=this.lastTime==null?0:Math.min(Math.max(0,time-this.lastTime),64);this.lastTime=time;
    const width=this.loopPixels;
    // Keep subpixel progress between frames. Reading a rounded scrollLeft back
    // every frame can discard the entire movement on high-refresh displays.
    if(width>0){this.autoPosition=(this.autoPosition+elapsed*.025)%width;this.viewport.scrollLeft=this.autoPosition;}
    this.frame=requestAnimationFrame(this.tick);
   };
   if('IntersectionObserver' in window){this.observer=new IntersectionObserver(entries=>{this.visible=entries[0].isIntersecting;this.sync();});this.observer.observe(this);}
   this.sync();
  }
  loopWidth(){return this.loopPixels||0;}
  pauseTemporarily(){
   clearTimeout(this.resumeTimer);this.temporaryPaused=true;this.sync();
   this.resumeTimer=setTimeout(()=>{this.resumeTimer=null;this.temporaryPaused=false;this.sync();},4000);
  }
  move(direction){
   this.pauseTemporarily();
   const distance=Math.max(180,this.viewport.clientWidth*.7),width=this.loopWidth();
   // Move to the equivalent loop position so Back also works at the first logo.
   if(width>0){
    if(direction<0&&this.viewport.scrollLeft<distance)this.viewport.scrollLeft+=width;
    else if(direction>0&&this.viewport.scrollLeft>=width)this.viewport.scrollLeft-=width;
   }
   this.viewport.scrollBy({left:direction*distance,behavior:this.motion.matches?'auto':'smooth'});
  }
  sync(){
   cancelAnimationFrame(this.frame);this.frame=null;this.lastTime=null;
   // Native arrows, wheel and touch can change the visible position while
   // paused; resume from that position instead of a stale animation target.
   this.autoPosition=this.viewport.scrollLeft;
   if(this.connected&&this.automatic&&!this.motion.matches&&!this.temporaryPaused&&!this.pointerHeld&&!this.hovered&&!this.focused&&this.visible&&!document.hidden)this.frame=requestAnimationFrame(this.tick);
  }
  setHoveredLogo(item){
   if(item===this.hoveredLogo)return;
   this.hoveredLogo?.removeAttribute('data-logo-hovered');this.hoveredLogo=item;
   item?.setAttribute('data-logo-hovered','');
  }
  disconnectedCallback(){this.connected=false;cancelAnimationFrame(this.frame);clearTimeout(this.resumeTimer);this.observer?.disconnect();this.resizeObserver?.disconnect();this.listeners?.forEach(remove=>remove());this.setHoveredLogo(null);this.clone?.remove();}
 });
})();
